import { useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, Settings2, LineChart, X } from "lucide-react";
import { carryTo } from "@/lib/carryParams";
import { useT } from "@/lib/i18n";
import { MONITOR, MANAGE, ANALYZE } from "@/lib/navTree";

/**
 * A dismissible "how this console is organized" card, mounted once on
 * Overview (G10). It orients a first-time operator around the three modes
 * (Monitor / Manage / Analyze) and self-destructs on first dismiss.
 *
 * Dismissal uses a GLOBAL localStorage key — NOT useUiPref, which is
 * per-site (store/uiPrefs.ts) and would re-show the card on every new or
 * switched site. Every storage touch is try/catch-guarded so a fully
 * sandboxed TB iframe degrades to "shows once per load" rather than
 * throwing.
 */
const WELCOME_KEY = "ems-welcome-dismissed";

export function WelcomeCard() {
  const t = useT();
  // Lazy initialiser so the storage read runs once, not every render.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WELCOME_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(WELCOME_KEY, "1");
    } catch {
      /* sandboxed storage — hide for this session anyway */
    }
    setDismissed(true);
  };

  // Each row's destination is DERIVED from the nav tree's mode defaultPath so
  // the primer can never drift from where the mode pill / palette / g-chord
  // actually land (N07). Icons stay hand-curated — ModeDef has no icon field.
  const rows: { to: string; icon: typeof Gauge; label: string }[] = [
    { to: carryTo(MONITOR.defaultPath), icon: Gauge, label: t("monitor.welcome.monitor") },
    { to: carryTo(MANAGE.defaultPath), icon: Settings2, label: t("monitor.welcome.manage") },
    { to: carryTo(ANALYZE.defaultPath), icon: LineChart, label: t("monitor.welcome.analyze") },
  ];

  return (
    <section role="note" aria-label={t("monitor.welcome.aria")} className="card relative overflow-hidden p-4">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("common.close")}
        className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-md text-text-faint transition hover:bg-surface-2 hover:text-text"
      >
        <X size={14} />
      </button>
      <div className="pr-7 text-[11px] font-semibold uppercase tracking-wider text-text-faint">{t("monitor.welcome.title")}</div>
      <div className="mt-0.5 text-sm text-text">{t("monitor.welcome.lede")}</div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {rows.map(({ to, icon: Icon, label }) => {
          const [mode, intent] = label.split(" — ");
          return (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-2 rounded-md border border-edge bg-surface px-2.5 py-2 text-xs transition hover:border-accent hover:text-accent"
            >
              <Icon size={15} strokeWidth={1.7} className="shrink-0 text-text-muted" />
              <span>
                <b className="font-semibold text-text">{mode}</b>
                {intent ? ` — ${intent}` : ""}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
